import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { Badge } from "@/components/ui/badge";
import { Leaf, TreePine, Car, Share2, DollarSign, Check, ExternalLink } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import BadgeEmbed from "@/components/BadgeEmbed";
import TeamSeats from "@/components/TeamSeats";

const COLORS = ["#2C5530", "#D97757", "#4A90A4", "#E5B24E", "#8E5EA2"];

export default function Dashboard() {
  const { user } = useAuth();
  const { format } = useCurrency();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/orders").then((r) => setOrders(r.data));
  }, []);

  const shareCert = async (certId) => {
    const url = `${window.location.origin}/certificate/${certId}`;
    if (navigator.share) {
      try { await navigator.share({ title: "My Carbon Vaults certificate", url }); return; } catch {}
    }
    navigator.clipboard.writeText(url);
    setCopiedId(certId);
    toast.success("Certificate link copied");
    setTimeout(() => setCopiedId(null), 2500);
  };

  if (!stats) return <div className="p-12 text-stone-500">Loading…</div>;

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-stone-500">Dashboard</div>
          <h1 className="text-4xl tracking-tighter font-light mt-2" data-testid="dashboard-title">Hello, {user?.company_name}</h1>
          <p className="text-stone-600 mt-1">Your climate portfolio at a glance.</p>
        </div>
        <Link to="/marketplace">
          <Button className="bg-[#2C5530] hover:bg-[#1F3D22] text-white rounded-full h-11 px-6">Buy more credits</Button>
        </Link>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard testid="stat-total-tons" icon={Leaf} label="Total tons offset" value={`${stats.total_tons.toLocaleString()} t`} accent="#2C5530"/>
        <StatCard testid="stat-total-spent" icon={DollarSign} label="Invested" value={format(stats.total_spent)} accent="#D97757"/>
        <StatCard testid="stat-trees" icon={TreePine} label="Trees equivalent" value={stats.trees_equivalent.toLocaleString()} accent="#2C5530"/>
        <StatCard testid="stat-cars" icon={Car} label="Cars off road (1yr)" value={stats.cars_off_road.toLocaleString()} accent="#D97757"/>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 bg-white border border-stone-200 rounded-2xl p-6">
          <div className="text-sm font-medium mb-4">Offset over time</div>
          {stats.monthly.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-stone-500 text-sm">No purchases yet — <Link to="/marketplace" className="text-[#2C5530] ml-1">start now</Link></div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={stats.monthly}>
                <XAxis dataKey="month" stroke="#78716C" fontSize={12}/>
                <YAxis stroke="#78716C" fontSize={12}/>
                <Tooltip contentStyle={{ background:"white", border:"1px solid #E7E5E4", borderRadius:12 }}/>
                <Line type="monotone" dataKey="tons" stroke="#2C5530" strokeWidth={2.5} dot={{ r: 4, fill:"#2C5530" }}/>
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="bg-white border border-stone-200 rounded-2xl p-6">
          <div className="text-sm font-medium mb-4">By category</div>
          {stats.by_category.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-stone-500 text-sm">No data</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={stats.by_category} dataKey="tons" nameKey="category" innerRadius={50} outerRadius={90} paddingAngle={4}>
                  {stats.by_category.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]}/>)}
                </Pie>
                <Legend/>
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Certificates */}
      <div className="bg-white border border-stone-200 rounded-2xl">
        <div className="p-6 border-b border-stone-200 flex items-center justify-between">
          <div className="text-sm font-medium">Certificates & orders</div>
          <span className="text-xs text-stone-500">{orders.length} total</span>
        </div>
        {orders.length === 0 ? (
          <div className="p-8 text-stone-500 text-sm">No purchases yet.</div>
        ) : (
          <div className="divide-y divide-stone-200">
            {orders.map((o) => (
              <div key={o.id} data-testid={`order-${o.id}`} className="p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <div className="font-medium">{o.project_name}</div>
                  <div className="text-xs text-stone-500 mt-1">Certificate {o.certificate_id} · {new Date(o.created_at).toLocaleDateString()}</div>
                </div>
                <div className="flex items-center gap-4">
                  <Badge variant="outline" className="border-stone-200">{o.tons} t CO₂</Badge>
                  <div className="text-sm">{format(o.amount)}</div>
                  <Link to={`/certificate/${o.certificate_id}`}>
                    <Button variant="ghost" size="sm" className="text-[#2C5530]" data-testid={`view-cert-${o.certificate_id}`}>
                      <ExternalLink className="w-4 h-4 mr-1"/> View
                    </Button>
                  </Link>
                  <Button variant="ghost" size="sm" className="text-[#2C5530]" onClick={() => shareCert(o.certificate_id)} data-testid={`share-cert-${o.certificate_id}`}>
                    {copiedId === o.certificate_id ? <><Check className="w-4 h-4 mr-1"/>Copied</> : <><Share2 className="w-4 h-4 mr-1"/>Share</>}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <BadgeEmbed />
      <TeamSeats />
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, accent, testid }) => (
  <div data-testid={testid} className="bg-white border border-stone-200 rounded-2xl p-5">
    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${accent}15`, color: accent }}>
      <Icon className="w-5 h-5"/>
    </div>
    <div className="mt-4 text-xs uppercase tracking-widest text-stone-500">{label}</div>
    <div className="mt-1 text-2xl font-light tracking-tight">{value}</div>
  </div>
);
